import {
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import { UndoRedoButtons } from '@src/components/common/UndoRedoButtons';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import ArrowDown from '@src/svgs/ArrowDown';
import ArrowUp from '@src/svgs/ArrowUp';
import BookIcon from '@src/svgs/BookIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import ListViewIcon from '@src/svgs/ListViewIcon';
import SearchIconOutline from '@src/svgs/SearchIconOutline';
import SparkleIcon from '@src/svgs/SparkleIcon';
import VideoIcon from '@src/svgs/VideoIcon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

/** Wide enough for a label over a destination line; the 26px trigger cannot set it. */
const CHAPTER_MENU_WIDTH = 220;

interface ReelEditorUtilityBarProps {
  /** The live query; the empty string means no filter is applied. */
  query: string;
  onQueryChange: (query: string) => void;
  /**
   * How many places in the reel the query was found, and which one the playhead
   * was last taken to — the transcript search's "n of m", over Sections.
   */
  matchCount: number;
  activeMatchIndex: number;
  /** Moves the playhead to the next/previous match, wrapping at either end. */
  onNextMatch: () => void;
  onPreviousMatch: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Omit and the bar shows no Add Clips — how the loading state renders. */
  onAddClips?: () => void;
  /**
   * PLAT-11098 — opens a chapter before the Section the playhead is in. Omit and
   * the bar shows no chapter control, the same way `onAddClips` is omitted while
   * the reel loads.
   */
  onAddChapter?: () => void;
  /**
   * The Section that boundary would open before, for the item's second line —
   * the destination stated in the menu rather than in a tooltip too long to
   * read (DS2).
   */
  chapterDestination?: string;
  /**
   * Present means the boundary is not representable, and says why (DS3). One
   * prop rather than a flag beside a reason, so the two cannot disagree.
   */
  chapterDisabledReason?: string;
  /**
   * PLAT-11099 — opens the Auto-Chapter overlay. A second item in the chapter
   * menu PLAT-11098 built, which said the AI item would be a pure addition to
   * it. Omit and the item is absent, the convention every optional control on
   * this bar follows: with no Dato client there is no prompt record to resolve.
   */
  onAutoChapter?: () => void;
  /**
   * Present means a chapterization is already running, and says so. The item
   * stands down rather than queueing a second read of the whole reel.
   */
  autoChapterDisabledReason?: string;
  /**
   * PLAT-11203 — takes the chapter headings off the list and puts them back.
   * Omit and the item is absent: a reel with no chapters has nothing to hide
   * (DS6), the convention every optional item in this menu follows.
   */
  onToggleChaptersHidden?: () => void;
  /** Which of the item's two labels reads — hide, or show again. */
  areChaptersHidden?: boolean;
  /**
   * PLAT-11204 — clears every chapter off the reel, behind the panel's confirm.
   * Omitted on a reel with no chapters, as above.
   */
  onRemoveAllChapters?: () => void;
  /** PLAT-11146 — binds the undo/redo hotkeys. On `window`, so only the open
   * tab may set it. */
  hotkeys?: boolean;
  tooltipId?: string;
}

/**
 * PLAT-11074 — the Reel Editor's utility bar, in the row the panel's title used
 * to occupy (DS1): search, undo/redo, then Add Clips, flush against each other
 * at the left and the search field floating below — the transcript header's own
 * layout, so the two panels read as one surface and further controls append to
 * the right.
 *
 * Undo and redo are the clip's single history reached from a second place, not
 * a second history (DS2) — this component owns neither, it renders the shared
 * buttons over whatever the panel passes down.
 *
 * PLAT-11146 — and their hotkeys, since the buttons were the only way to reach
 * that history here: the Transcript binds Cmd+Z on its own scroll container, so
 * with focus anywhere in this panel nothing was listening and Cmd+Z did nothing
 * at all. The binding is on `window` — so a drag that left focus on the dropped
 * row is covered — and only the open tab may set it (`hotkeys`): the panel is
 * display-toggled rather than unmounted, so it outlives its own tab.
 */
export const ReelEditorUtilityBar: React.FC<ReelEditorUtilityBarProps> = ({
  query,
  onQueryChange,
  matchCount,
  activeMatchIndex,
  onNextMatch,
  onPreviousMatch,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onAddClips,
  onAddChapter,
  chapterDestination,
  chapterDisabledReason,
  onAutoChapter,
  autoChapterDisabledReason,
  onToggleChaptersHidden,
  areChaptersHidden,
  onRemoveAllChapters,
  hotkeys = false,
  tooltipId,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // PLAT-11098 — the chapter menu. Portaled and self-dismissing through the
  // shared hook, which is what the gap's own menu uses (PLAT-11068 DS5): the
  // panel sits inside the clip-preview modal, so the menu needs the
  // above-the-backdrop z-tier, and the hook owns outside click, Escape, scroll
  // and resize rather than four hand-rolled listeners.
  const [isChapterMenuOpen, setIsChapterMenuOpen] = useState(false);
  const chapterTriggerRef = useRef<HTMLButtonElement>(null);
  const closeChapterMenu = useCallback(() => setIsChapterMenuOpen(false), []);
  const { menuRef: chapterMenuRef, style: chapterMenuStyle } =
    useAnchoredDropdown(
      isChapterMenuOpen,
      chapterTriggerRef,
      closeChapterMenu,
      undefined,
      { width: CHAPTER_MENU_WIDTH },
    );

  useEffect(() => {
    if (!isSearchOpen) return;
    // Focusing the renderer iframe's sibling is safe here — the guard in
    // CLAUDE.md pitfall 10 is about stealing focus *from* the canvas, and this
    // only runs on a click the creator just made in this panel.
    inputRef.current?.focus();
  }, [isSearchOpen]);

  // Closing always clears: a filter left applied behind a collapsed input is a
  // panel that silently lies about the reel (DS7).
  const closeSearch = () => {
    setIsSearchOpen(false);
    onQueryChange('');
  };

  return (
    <Bar data-testid="reel-editor-utility-bar">
      <BarButton
        type="button"
        $isPressed={isSearchOpen}
        aria-label={isSearchOpen ? 'Close search' : 'Search the reel'}
        data-testid="reel-editor-search-toggle"
        data-tooltip-id={tooltipId}
        data-tooltip-content={isSearchOpen ? 'Close search' : 'Search the reel'}
        onClick={() => (isSearchOpen ? closeSearch() : setIsSearchOpen(true))}
      >
        <SearchIconOutline />
      </BarButton>

      <UndoRedoButtons
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={onUndo}
        onRedo={onRedo}
        setHotkeys={hotkeys}
        hoverColor="var(--accent)"
        buttonSize={22}
      />

      {onAddClips && (
        <BarButton
          type="button"
          $isPressed={false}
          // Icon-only, so the name lives here rather than on screen.
          aria-label="Add clip"
          data-testid="reel-editor-add-clips"
          data-tooltip-id={tooltipId}
          // Names the control, not its destination: the playhead's landing spot
          // runs to a sentence, and a tooltip that long covers the summary and
          // the first chapter heading beneath it. The picker states it on open
          // instead, where there is room for it (PLAT-11057 DS1).
          data-tooltip-content="Add Clip"
          onClick={onAddClips}
        >
          {/* The same clapperboard the gap menu's Add Clip(s) carries, so the
              two ways into the picker are one glyph. `currentColor` over its
              gold default, to inherit the bar's resting and hover colors. */}
          <VideoIcon strokeColor="currentColor" />
        </BarButton>
      )}

      {/* PLAT-11098 — a boundary at the playhead, appended to the right of Add
          Clip because that is where `Bar`'s flex-start says a new control goes
          (DS5). One click from wherever the creator is listening, instead of
          hunting the 13px gap between two rows. */}
      {(onAddChapter ||
        onAutoChapter ||
        onToggleChaptersHidden ||
        onRemoveAllChapters) && (
        <BarButton
          ref={chapterTriggerRef}
          type="button"
          $isPressed={isChapterMenuOpen}
          aria-haspopup="menu"
          aria-expanded={isChapterMenuOpen}
          // Icon-only, so the name lives here rather than on screen.
          aria-label="Add a chapter"
          data-testid="reel-editor-add-chapter"
          data-tooltip-id={tooltipId}
          // Names the control, not its destination — the Section the boundary
          // opens before runs to a sentence, and a tooltip that long covers the
          // summary and the first chapter heading beneath it (PLAT-11057 DS1).
          // The menu states it instead, where there is a second line for it.
          // Emptied rather than dropped while the menu is open: dropping the
          // *id* sends react-tooltip looking for another anchor sharing it and
          // it takes the first in the document — the search button (PLAT-11068).
          data-tooltip-content={isChapterMenuOpen ? '' : 'Add Chapter'}
          onClick={() => setIsChapterMenuOpen(open => !open)}
        >
          {/* The same book the gap menu's Add Chapter carries, so the two ways
              to a boundary are one glyph. `currentColor` to inherit the bar's
              resting and hover colors. Sized down from the icon's own 24px
              default to the 16px every other glyph in this row runs at — at 24
              it overflowed the bar's 18px content box and sat taller than
              search, undo/redo and Add Clip. */}
          <BookIcon width="16" height="16" strokeColor="currentColor" />
        </BarButton>
      )}

      {isChapterMenuOpen &&
        chapterMenuStyle &&
        createPortal(
          <ContextMenuSurface
            ref={chapterMenuRef}
            style={chapterMenuStyle}
            role="menu"
            data-testid="reel-editor-chapter-menu"
          >
            {onAddChapter && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                $disabled={!!chapterDisabledReason}
                // Still hoverable while disabled, so the native tooltip can say
                // why (DS3) — a control that vanishes reads as a bug.
                title={chapterDisabledReason}
                aria-disabled={!!chapterDisabledReason || undefined}
                data-testid="reel-editor-chapter-menu-add"
                onClick={() => {
                  if (chapterDisabledReason) return;
                  closeChapterMenu();
                  onAddChapter();
                }}
              >
                <BookIcon strokeColor="currentColor" />
                <MenuLabel>
                  Add Chapter
                  {chapterDestination && (
                    <MenuDestination data-testid="reel-editor-chapter-menu-destination">
                      before “{chapterDestination}”
                    </MenuDestination>
                  )}
                </MenuLabel>
              </ContextMenuItem>
            )}

            {/* PLAT-11099 — the second item PLAT-11098 DS2 left room for. The
                ellipsis says it opens something rather than running: chaptering
                a whole reel is a request with options, and the options are the
                point (DS2). */}
            {onAutoChapter && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                $disabled={!!autoChapterDisabledReason}
                title={autoChapterDisabledReason}
                aria-disabled={!!autoChapterDisabledReason || undefined}
                data-testid="reel-editor-chapter-menu-auto"
                onClick={() => {
                  if (autoChapterDisabledReason) return;
                  closeChapterMenu();
                  onAutoChapter();
                }}
              >
                {/* The glyph this codebase already reads as "AI wrote this",
                    the same one both naming items carry. */}
                <SparkleIcon
                  width="14"
                  height="14"
                  strokeColor="currentColor"
                />
                <MenuLabel>Auto-Chapter with AI…</MenuLabel>
              </ContextMenuItem>
            )}

            {/* PLAT-11203 — the headings off the list and back on. No
                confirmation and no destination line: it changes what is on
                screen and nothing about the reel, so the item is the shortest
                row in the menu. */}
            {onToggleChaptersHidden && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                data-testid="reel-editor-chapter-menu-visibility"
                onClick={() => {
                  closeChapterMenu();
                  onToggleChaptersHidden();
                }}
              >
                {/* Not an eye: the eye pair means "in the reel or not", which
                    is what Turn Section Off two menus over does. This row
                    changes only what the panel draws, so it carries the view it
                    produces — chapters off is a flat list of Sections — and
                    keeps the one glyph in both directions, the label carrying
                    which way it goes (design-system.md, Icon Semantics). */}
                <ListViewIcon strokeColor="currentColor" />
                <MenuLabel>
                  {areChaptersHidden ? 'Show Chapters' : 'Hide Chapters'}
                </MenuLabel>
              </ContextMenuItem>
            )}

            {/* PLAT-11204 — the one destructive row in this menu, and it reads
                as one before it is clicked. The confirm behind it is the
                panel's (DS2); this item only asks for it. */}
            {onRemoveAllChapters && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                $danger
                data-testid="reel-editor-chapter-menu-remove-all"
                onClick={() => {
                  closeChapterMenu();
                  onRemoveAllChapters();
                }}
              >
                {/* Unsized, the way the Section menu's own Remove renders it:
                    `ContextMenuItem` normalizes every icon in it to 15px. */}
                <DeleteIcon />
                <MenuLabel>Remove All Chapters</MenuLabel>
              </ContextMenuItem>
            )}
          </ContextMenuSurface>,
          document.body,
        )}

      {/* The field opens in the row *below* the controls — the summary's row —
          anchored to the bar rather than to the search button, so it lands at
          the panel's left edge and the controls above it never move. Absolute,
          so opening search covers the summary for as long as it is open
          instead of pushing the whole list down. */}
      {isSearchOpen && (
        <FloatingSearch>
          <SearchField>
            <SearchInput
              ref={inputRef}
              type="text"
              value={query}
              placeholder="Search clips"
              spellCheck={false}
              autoComplete="off"
              aria-label="Search the reel"
              data-testid="reel-editor-search-input"
              onChange={e => onQueryChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  closeSearch();
                } else if (e.key === 'Enter') {
                  // The field is not in a form, but the transcript's search
                  // stops the default here too — a stray submit would reload
                  // the app out from under an unsaved reel.
                  e.preventDefault();
                  if (e.shiftKey) onPreviousMatch();
                  else onNextMatch();
                }
              }}
              // An empty input that has lost the pointer has nothing to say;
              // one holding a query stays, because the list beneath it is
              // filtered.
              onBlur={() => {
                if (!query) setIsSearchOpen(false);
              }}
            />
            {/* The transcript search's own readout and walk buttons, over reel
                Sections instead of paragraphs: how many places the query was
                found, which one the playhead is at, and a step either way. */}
            {query && (
              <>
                <MatchCount data-testid="reel-editor-search-count">
                  {matchCount > 0
                    ? `${activeMatchIndex + 1} of ${matchCount}`
                    : '0 results'}
                </MatchCount>
                {matchCount > 0 && (
                  <>
                    <NavButton
                      type="button"
                      // Focus stays in the input: the creator is still typing,
                      // and Enter has to keep walking from where a click left
                      // off.
                      tabIndex={-1}
                      aria-label="Previous match"
                      data-testid="reel-editor-search-prev"
                      title="Previous match (Shift+Enter)"
                      onClick={onPreviousMatch}
                    >
                      <ArrowUp
                        width="8"
                        height="5"
                        viewBox="0 0 18 11"
                        strokeColor="currentColor"
                        strokeWidth="2.5"
                      />
                    </NavButton>
                    <NavButton
                      type="button"
                      tabIndex={-1}
                      aria-label="Next match"
                      data-testid="reel-editor-search-next"
                      title="Next match (Enter)"
                      onClick={onNextMatch}
                    >
                      <ArrowDown
                        width="8"
                        height="5"
                        viewBox="0 0 18 11"
                        strokeColor="currentColor"
                        strokeWidth="2.5"
                      />
                    </NavButton>
                  </>
                )}
              </>
            )}
            {query && (
              <ClearButton
                type="button"
                aria-label="Clear search"
                data-testid="reel-editor-search-clear"
                onClick={() => {
                  onQueryChange('');
                  inputRef.current?.focus();
                }}
              >
                <CloseIcon width={10} height={10} />
              </ClearButton>
            )}
          </SearchField>
        </FloatingSearch>
      )}
    </Bar>
  );
};

/* The transcript header's `Buttons`, copied: 26px tall, 8px between controls,
   2px of tail padding — one row of controls flush against each other at the
   LEFT. `justify-content` is declared rather than left to the default so no
   ancestor rule or later control can spread the row apart; everything the bar
   gains appends to the right of Add Clips. */
const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  height: 26px;
  flex-shrink: 0;
  padding-right: 2px;
  /* What the search field positions against — see FloatingSearch. */
  position: relative;
`;

/* The search field, in the row under the controls rather than inside them: an
   inline field would widen and shove undo/redo along every time the creator
   searched, which is exactly what it used to do. Anchored to the bar's own
   left edge, and offset by the header's 4px row gap so it sits on the summary
   line. */
const FloatingSearch = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  z-index: 100001;
  /* Out of flow, so it has no width to inherit — without one the field would
     shrink to the input's default size. Wide enough for a phrase, narrow
     enough to sit inside the panel. */
  width: 240px;
  height: 26px;
`;

/**
 * The transcript header's `PanelButton`, in the one size this bar uses — shared
 * by every icon control here so search and Add Clips are the same button with
 * different glyphs, not two lookalikes.
 */
const BarButton = styled.button<{ $isPressed: boolean }>`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 4px;
  border: none;
  border-radius: var(--radius-button);
  cursor: pointer;
  background-color: ${props =>
    props.$isPressed ? 'var(--surface-elevated)' : 'transparent'};
  color: ${props =>
    props.$isPressed ? 'var(--accent)' : 'var(--text-secondary)'};
  transition: all 150ms ease-out;

  &:hover {
    background-color: var(--surface-elevated);
    color: ${props =>
      props.$isPressed ? 'var(--accent)' : 'var(--foreground)'};
  }

  &:active {
    scale: 0.98;
  }
`;

/**
 * PLAT-11098 — the chapter item's two lines. `ContextMenuItem` lays its children
 * out in a row against the middle, so the label and its destination need one
 * column of their own for the icon to sit against the pair rather than the first
 * line. `min-width: 0` so the destination below can ellipsise.
 */
const MenuLabel = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

/**
 * Where the boundary would land. Muted helper text in sentence case under a
 * Title Case label (`docs/design-system.md`), and held to one line: a Section
 * named by hand can run long, and the menu's width is fixed.
 */
const MenuDestination = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  font-weight: var(--font-weight-normal);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Bordered field primitive: the border carries every state, and focus is the
   neutral gray tokens — never green or gold (docs/design-system.md). */
const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  /* Fills the anchor, which is what carries the width now that the field
     floats rather than stretching the row. */
  width: 100%;
  height: 100%;
  padding: 0 6px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background-color: var(--input-background);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus-within {
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

/* "3 of 12", in the transcript search's own muted readout. Tabular, so the
   digits do not shuffle the buttons beside them as the walk counts up. */
const MatchCount = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  user-select: none;
`;

/* The walk buttons, on the clear button's chrome so the three controls at the
   field's right edge are one family. */
const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--foreground);
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--foreground);
  }
`;
